import React, { useState, useMemo } from 'react';
import { StatusDot } from '@/components/shared/StatusDot';
import { Badge } from '@/components/shared/Badge';
import { StrategyCard } from '@/components/cards/StrategyCard';
import type { Bot, StrategyGroupName } from '@/types/bot';
import './CenterPanel.css';

interface CenterPanelProps {
  bots: Bot[];
}

interface GroupedBots {
  name: StrategyGroupName;
  hasActive: boolean;
  bots: Bot[];
}

export const CenterPanel: React.FC<CenterPanelProps> = ({ bots }) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    const map = new Map<StrategyGroupName, Bot[]>();
    for (const bot of bots) {
      const existing = map.get(bot.strategyGroup) || [];
      existing.push(bot);
      map.set(bot.strategyGroup, existing);
    }

    const result: GroupedBots[] = Array.from(map.entries()).map(([name, groupBots]) => ({
      name,
      hasActive: groupBots.some(b => b.status === 'active'),
      bots: [...groupBots].sort((a, b) => {
        // Active first, then by PnL descending
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        return (b.performance?.todayPnl || 0) - (a.performance?.todayPnl || 0);
      }),
    }));

    // Sort groups: active groups first
    return result.sort((a, b) => {
      if (a.hasActive && !b.hasActive) return -1;
      if (!a.hasActive && b.hasActive) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [bots]);

  const toggle = (name: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div className="center-panel">
      <div className="center-panel__header">
        <StatusDot color="green" />
        <span>Live</span>
        <span className="center-panel__group-count">{bots.length} bots &middot; {groups.length} groups</span>
      </div>

      <div className="center-panel__cards">
        {groups.map((group) => {
          const isCollapsed = collapsed.has(group.name);
          return (
            <div key={group.name} className="center-panel__group">
              <div
                className="center-panel__group-header"
                onClick={() => toggle(group.name)}
              >
                <span className="center-panel__group-chevron">
                  {isCollapsed ? '\u25B6' : '\u25BC'}
                </span>
                <span className="center-panel__group-name">{group.name}</span>
                <span className="center-panel__group-count">{group.bots.length}</span>
                {group.hasActive && <Badge variant="success">LIVE</Badge>}
              </div>
              {!isCollapsed && group.bots.map((bot) => (
                <StrategyCard key={bot.id} bot={bot} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};
